import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Yapılmadı', color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' },
  { value: 'informed', label: 'Arandı', color: 'text-blue-500', bg: 'bg-blue-50', dot: 'bg-blue-500' },
  { value: 'completed', label: 'Yapıldı', color: 'text-green-600', bg: 'bg-green-50', dot: 'bg-green-500' },
  { value: 'reschedule', label: 'Tarih Değiştir', color: 'text-orange-500', bg: 'bg-orange-50', dot: 'bg-orange-500' },
];

const StatusDropdown = ({ status = 'pending', onChange, compact = false, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const current = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 140;
      let left = rect.left;
      if (left + menuWidth > window.innerWidth - 8) {
        left = rect.right - menuWidth;
      }
      setPos({ top: rect.bottom + 4, left });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = (e) => {
    e.stopPropagation();
    updatePos();
    setOpen(!open);
  };

  const handleSelect = (value) => {
    setOpen(false);
    onChange(value);
  };

  return (
    <>
      {status === 'not_approved' ? (
        <span className={compact
          ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[9px] font-semibold text-orange-500 bg-orange-50 border-orange-300'
          : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold text-orange-500 bg-orange-50 border-orange-300'
        } data-testid="status-not-approved">
          <span className={`${compact ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full bg-orange-500`} />
          Onaylanmadı
        </span>
      ) : (
      <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className={compact
          ? `inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[9px] font-semibold transition-colors ${current.color} ${current.bg} ${
              status === 'completed' ? 'border-green-300' : status === 'reschedule' ? 'border-orange-300' : status === 'informed' ? 'border-blue-300' : 'border-red-300'
            } hover:opacity-80`
          : `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${current.color} ${current.bg} ${
              status === 'completed' ? 'border-green-300' : status === 'reschedule' ? 'border-orange-300' : status === 'informed' ? 'border-blue-300' : 'border-red-300'
            } hover:opacity-80`
        }
        data-testid="status-trigger"
      >
        <span className={`${compact ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full ${current.dot}`} />
        {current.label}
        <ChevronDown size={compact ? 8 : 10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed bg-white rounded-xl shadow-lg border border-gray-200 min-w-[140px] py-1 animate-in fade-in slide-in-from-top-1"
          style={{ top: pos.top, left: pos.left, zIndex: 99999 }}
          data-testid="status-options"
        >
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-medium hover:bg-gray-50 transition-colors ${
                opt.value === status ? 'bg-gray-50' : ''
              }`}
              data-testid={`status-option-${opt.value}`}
            >
              <span className={`w-2 h-2 rounded-full ${opt.dot}`} />
              <span className={opt.color}>{opt.label}</span>
              {opt.value === status && <Check size={12} className="ml-auto text-[#6b6de3]" />}
            </button>
          ))}
        </div>,
        document.body
      )}
      </>
      )}
    </>
  );
};

export default StatusDropdown;
